import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  IconButton,
  Menu,
} from "@mui/material";
import StyledInvoice from "./StyledInvoice";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useMediaQuery } from "@mui/material";
import InvoicePage from "./InvoicePage";
import { fetchInvoices } from "../../store/reducers/invoices";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import rollbar from "../../rollbarConfig";

const InvoiceSection = () => {
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState("placeholder");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [searchId, setSearchId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceList, setInvoiceList] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [count, setCount] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);

  const getInvoices = async () => {
    setLoading(true);
    const query = `?start_date=${startDate}&order_number=${searchId}&end_date=${endDate}&page_no=${count}&page_size=${pageSize}`;
    try {
      const res = await dispatch(fetchInvoices(query));
      const { status, _meta, data } = res?.payload;
      if (status) {
        setInvoiceList(data);
        setPaginationData(_meta);
      }
    } catch (err) {
      console.error("err :", err);
      rollbar.error("Error message", err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value) => {
    setSelectedValue(value);

    const today = dayjs().format("YYYY-MM-DD");
    let start = today;
    let end = today;

    if (value === "last_7_days") {
      start = dayjs().subtract(7, "day").format("YYYY-MM-DD");
    } else if (value === "last_30_days") {
      start = dayjs().subtract(30, "day").format("YYYY-MM-DD");
    } else if (value === "last_3_months") {
      start = dayjs().subtract(3, "month").format("YYYY-MM-DD");
    }

    setStartDate(start);
    setEndDate(end);
    handleCloseMenu();
  };

  const clearFilter = (event) => {
    event.stopPropagation();
    setSelectedValue("placeholder");
    setStartDate("");
    setEndDate("");
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      getInvoices();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchId, startDate, endDate, pageSize, count]);

  // Menu handlers
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <StyledInvoice>
      <Box className="search-filter-container">
        {/* Search Box */}
        <TextField
          variant="outlined"
          placeholder="Search by order id"
          className="search-box"
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="searchIcon" />
              </InputAdornment>
            ),
          }}
        />

        {/* Filter Dropdown */}
        {isMobile ? (
          <>
            <Button
              variant="outlined"
              className="mobile-filter-button"
              startIcon={<FilterListIcon className="filter-icon" />}
              onClick={handleOpenMenu}
            >
              <Typography className="filter">Filter</Typography>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem disabled>
                <span className="placeholder">Filter by date range</span>
              </MenuItem>
              <MenuItem className="select-data" onClick={() => handleFilterChange("last_7_days")}>
                Last 7 Days
              </MenuItem>
              <MenuItem className="select-data" onClick={() => handleFilterChange("last_30_days")}>
                Last 30 Days
              </MenuItem>
              <MenuItem className="select-data" onClick={() => handleFilterChange("last_3_months")}>
                Last 3 Months
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Select
            value={selectedValue}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="filter-box"
            IconComponent={KeyboardArrowDownIcon}
            displayEmpty
            fullWidth
            renderValue={(selected) =>
              selected !== "placeholder" ? (
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography className="filter-value">{selected.replace(/_/g, " ")}</Typography>
                  <IconButton
                    size="small"
                    onMouseDown={(event) => {
                      event.stopPropagation();
                      clearFilter(event);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <span className="placeholder">Filter by date range</span>
              )
            }
          >
            <MenuItem className="select-data" value="last_7_days">
              Last 7 Days
            </MenuItem>
            <MenuItem className="select-data" value="last_30_days">
              Last 30 Days
            </MenuItem>
            <MenuItem className="select-data" value="last_3_months">
              Last 3 Months
            </MenuItem>
          </Select>
        )}
      </Box>
      <Box className="main-container">
        <InvoicePage
          sxHeight="calc(100vh - 180px)"
          data={invoiceList}
          paginationData={paginationData}
          setPageSize={setPageSize}
          count={count}
          pageSize={paginationData?.total_count >= 10 ? pageSize : paginationData?.total_count}
          setCount={setCount}
          loading={loading}
        />
      </Box>
    </StyledInvoice>
  );
};

export default InvoiceSection;
